/**
 * DeckCardActions - Shared zone / commander / cover action buttons for deck cards.
 *
 * Rendered by both the list (table) view rows and the grid view cards so the
 * "move to zone", "set as commander", and "set as deck cover" controls behave
 * identically across views. Returns a fragment of buttons; the parent is
 * responsible for the surrounding layout container.
 */
import type { ReactElement } from 'react';
import { useCallback, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import clsx from 'clsx';
import { FaCrown, FaExchangeAlt, FaImage } from 'react-icons/fa';

import { calculateDropdownPosition } from '@/hooks/useDropdownPosition';
import { DECK_ZONE_LABELS, isCommanderFormat } from '@/lib/deckUtils';
import { canBeCommander, canMoveToZone, getAvailableZonesForCard } from '@/lib/deckValidation';
import type { DeckFormat, DeckStatus, DeckZone } from '@/types/deck';

import useDeckCardOperations from '../hooks/useDeckCardOperations';
import type { EnrichedDeckCard } from '../types';

export type DeckCardActionsVariant = 'row' | 'grid';

export interface DeckCardActionsProps {
  deckCard: EnrichedDeckCard;
  deckId: string;
  deckFormat: DeckFormat | null;
  deckStatus: DeckStatus;
  /** Whether this card is the current deck cover */
  isCover: boolean;
  isLocked?: boolean;
  /** Visual style: 'row' for table cells, 'grid' for grid hover overlay */
  variant?: DeckCardActionsVariant;
  onSetCover: (scryfallId: string) => void;
  onSetCommander?: (cardId: string, scryfallId: string) => void;
}

/** Per-variant button styling so the same actions fit each surface. */
const BUTTON_CLASSES: Record<DeckCardActionsVariant, string> = {
  row: 'shrink-0 rounded p-1.5 text-zinc-500 transition-all hover:bg-zinc-700 hover:text-zinc-300',
  grid: 'pointer-events-auto flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-zinc-800/90 text-zinc-300 transition-colors hover:bg-zinc-700 hover:text-zinc-100',
};

const COMMANDER_BUTTON_CLASSES: Record<DeckCardActionsVariant, string> = {
  row: 'shrink-0 rounded p-1.5 text-zinc-500 transition-all hover:bg-amber-500/20 hover:text-amber-400',
  grid: 'pointer-events-auto flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-zinc-800/90 text-zinc-300 transition-colors hover:bg-amber-500/20 hover:text-amber-400',
};

const COVER_ACTIVE_CLASSES: Record<DeckCardActionsVariant, string> = {
  row: 'text-teal bg-teal/20 shrink-0 rounded p-1.5',
  grid: 'text-teal bg-teal/20 pointer-events-auto flex h-8 w-8 shrink-0 items-center justify-center rounded-lg',
};

const COVER_BUTTON_CLASSES: Record<DeckCardActionsVariant, string> = {
  row: 'hover:text-teal shrink-0 rounded p-1.5 text-zinc-600 transition-all hover:bg-zinc-700',
  grid: 'hover:text-teal pointer-events-auto flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-zinc-800/90 text-zinc-300 transition-colors hover:bg-zinc-700',
};

export default function DeckCardActions({
  deckCard,
  deckId,
  deckFormat,
  deckStatus,
  isCover,
  isLocked = false,
  variant = 'row',
  onSetCover,
  onSetCommander,
}: DeckCardActionsProps): ReactElement | null {
  const { changeZone } = useDeckCardOperations({ deckId, isLocked, deckFormat });

  const { card, id: cardId, scryfall_id, zone: currentZone } = deckCard;

  const [showZoneDropdown, setShowZoneDropdown] = useState(false);
  const zoneButtonRef = useRef<HTMLButtonElement>(null);
  const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0 });

  // Check if card can be commander (only relevant for commander formats)
  const cardCanBeCommander = useMemo(() => {
    if (!card || !isCommanderFormat(deckFormat)) return false;
    return canBeCommander(card, deckFormat);
  }, [card, deckFormat]);

  // Determine available zones for moving this card using zone rules
  const availableZones = useMemo((): DeckZone[] => {
    if (!card) return ['main'];
    return getAvailableZonesForCard(card, deckFormat, deckStatus);
  }, [card, deckFormat, deckStatus]);

  const handleZoneChange = useCallback(
    (newZone: DeckZone) => {
      if (isLocked || newZone === currentZone) return;

      if (card) {
        const validation = canMoveToZone(card, newZone, deckFormat, deckStatus);
        if (!validation.allowed) {
          console.warn('Zone change blocked:', validation.reason);
          setShowZoneDropdown(false);
          return;
        }
      }

      if (newZone === 'commander' && onSetCommander) {
        onSetCommander(cardId, scryfall_id);
      } else {
        changeZone(cardId, newZone);
      }
      setShowZoneDropdown(false);
    },
    [changeZone, cardId, currentZone, isLocked, onSetCommander, scryfall_id, card, deckFormat, deckStatus],
  );

  const handleSetAsCommander = useCallback(() => {
    if (!cardCanBeCommander || isLocked || !onSetCommander) return;
    onSetCommander(cardId, scryfall_id);
  }, [cardCanBeCommander, isLocked, onSetCommander, cardId, scryfall_id]);

  if (isLocked || !card) return null;

  const iconClass = 'h-3.5 w-3.5';

  return (
    <>
      {/* Zone move dropdown */}
      {availableZones.length > 1 && (
        <div className="relative">
          <button
            ref={zoneButtonRef}
            type="button"
            onClick={() => {
              if (!showZoneDropdown && zoneButtonRef.current) {
                const pos = calculateDropdownPosition(zoneButtonRef.current, {
                  placement: 'bottom-end',
                  estimatedWidth: 144,
                  estimatedHeight: availableZones.length * 32 + 8,
                });
                setDropdownPos({ top: pos.top, left: pos.left });
              }
              setShowZoneDropdown(!showZoneDropdown);
            }}
            className={BUTTON_CLASSES[variant]}
            aria-label="Move to zone"
            title="Move to zone"
          >
            <FaExchangeAlt className={iconClass} />
          </button>

          {showZoneDropdown &&
            createPortal(
              <>
                {/* Backdrop to close dropdown */}
                <div className="fixed inset-0 z-9998" onClick={() => setShowZoneDropdown(false)} />
                {/* Dropdown - positioned absolutely relative to viewport */}
                <div
                  className="fixed z-9999 w-36 rounded-lg border border-zinc-700 bg-zinc-800 py-1 shadow-lg"
                  style={{ top: dropdownPos.top, left: dropdownPos.left }}
                >
                  {availableZones.map((zone) => (
                    <button
                      key={zone}
                      type="button"
                      onClick={() => handleZoneChange(zone)}
                      className={clsx(
                        'flex w-full items-center gap-2 px-3 py-1.5 text-left text-sm transition-colors',
                        zone === currentZone ? 'bg-zinc-700 text-zinc-200' : 'text-zinc-400 hover:bg-zinc-700/50 hover:text-zinc-200',
                      )}
                    >
                      {zone === 'commander' && <FaCrown className="h-3 w-3 text-amber-400" />}
                      <span>{DECK_ZONE_LABELS[zone]}</span>
                      {zone === currentZone && <span className="ml-auto text-xs text-zinc-500">✓</span>}
                    </button>
                  ))}
                </div>
              </>,
              document.body,
            )}
        </div>
      )}

      {/* Quick set as commander button (for valid cards not already commanders) */}
      {cardCanBeCommander && currentZone !== 'commander' && onSetCommander && (
        <button
          type="button"
          onClick={handleSetAsCommander}
          className={COMMANDER_BUTTON_CLASSES[variant]}
          aria-label="Set as Commander"
          title="Set as Commander"
        >
          <FaCrown className={iconClass} />
        </button>
      )}

      {/* Cover Button - active state when selected */}
      {isCover ? (
        <div className={COVER_ACTIVE_CLASSES[variant]} title="Current deck cover">
          <FaImage className={iconClass} />
        </div>
      ) : (
        <button
          type="button"
          onClick={() => onSetCover(scryfall_id)}
          className={COVER_BUTTON_CLASSES[variant]}
          aria-label="Set as cover"
          title="Set as deck cover"
        >
          <FaImage className={iconClass} />
        </button>
      )}
    </>
  );
}
